import React, { useState } from 'react';
import FileSaver from 'file-saver';

import RecordAction from '../RecordAction/RecordAction';

import restApi from '../../../../api';

import { recordActionPopups } from '../../helpers/constants';

import startIcon from '../../../../assets/img/star.svg';
import amDelete from '../../../../assets/img/accountmanagement/am-delete.svg';
import amDownload from '../../../../assets/img/accountmanagement/am-download.svg';
import amEdit from '../../../../assets/img/accountmanagement/am-edit.svg';
import amTeams from '../../../../assets/img/accountmanagement/am-teams.svg';
import amWarning from '../../../../assets/img/accountmanagement/am-warning.svg';

import styles from './AccountManagementRecord.module.css';

const getAccountManagementColor = percent => {
  if (percent < 30) {
    return '#f61553';
  }
  if (percent >= 30 && percent <= 75) {
    return '#f9ad2c';
  }
  if (percent > 75) {
    return '#7fc26c';
  }
  return null;
};

export default ({
  level,
  parent,
  record,
  setActivePopup,
  onRecordClick,
  accountManagement,
  isAdmin,
  actionsAllowed,
  higherRecords,
  onSelectRecord
}) => {
  const [isRecordHovered, setIsRecordHovered] = useState(false);

  let shouldBudgetOverviewBeAvailable = false;
  if (record && record.budgetAvailable === true) {
    if (isAdmin) {
      shouldBudgetOverviewBeAvailable = true;
    }

    // Only spoc should see download budget
    if (!shouldBudgetOverviewBeAvailable) {
      shouldBudgetOverviewBeAvailable =
        accountManagement &&
        accountManagement.manager &&
        accountManagement.manager.some(m => m.id === record.id);

      if (level === 2 && !shouldBudgetOverviewBeAvailable) {
        // SPOC should see download budget
        shouldBudgetOverviewBeAvailable =
          accountManagement &&
          accountManagement.manager &&
          accountManagement.manager.some(m => m.id === parent);
      }
    }
  }

  let licenceStartDateParsed = null;
  let licenceEndDateParsed = null;
  let creditsThatShouldSpendPerDay = null;

  if (level === 1) {
    licenceStartDateParsed =
      record && record.licenceStartDate
        ? new Date(record.licenceStartDate)
        : null;
    licenceEndDateParsed =
      record && record.licenceEndDate ? new Date(record.licenceEndDate) : null;
    creditsThatShouldSpendPerDay =
      record.creditsAllocated !== null ? record.creditsAllocated / 365 : null;
  }

  if (level === 2) {
    const parentRecordInformation = higherRecords.find(r => r.id === parent);

    if (parentRecordInformation) {
      licenceStartDateParsed = parentRecordInformation.licenceStartDate
        ? new Date(parentRecordInformation.licenceStartDate)
        : null;
      licenceEndDateParsed = parentRecordInformation.licenceEndDate
        ? new Date(parentRecordInformation.licenceEndDate)
        : null;
      creditsThatShouldSpendPerDay =
        record.creditsAllocated !== null ? record.creditsAllocated / 365 : null;
    }
  }

  const licenceStartDateString = licenceStartDateParsed
    ? `${licenceStartDateParsed.getDate()}/${licenceStartDateParsed.getMonth() +
        1}/${licenceStartDateParsed.getFullYear()}`
    : '';
  const licenceEndDateString = licenceStartDateParsed
    ? `${licenceEndDateParsed.getDate()}/${licenceEndDateParsed.getMonth() +
        1}/${licenceEndDateParsed.getFullYear()}`
    : '';

  const dateLeftToExpire = licenceEndDateParsed
    ? new Date().getTime() - licenceEndDateParsed.getTime()
    : null;
  const daysLeftToExpire = dateLeftToExpire
    ? dateLeftToExpire / (1000 * 3600 * 24)
    : null;
  const daysLeftToExpireInteger =
    daysLeftToExpire !== null ? Math.floor(Math.abs(daysLeftToExpire)) : null;

  const licenceDaysElapsed =
    creditsThatShouldSpendPerDay !== null
      ? 365 - daysLeftToExpireInteger
      : null;
  const creditsThatShouldHaveBeenSpent =
    licenceDaysElapsed !== null
      ? licenceDaysElapsed * creditsThatShouldSpendPerDay
      : null;
  const spentCredits =
    record.creditsAllocated !== null
      ? record.creditsAllocated - record.creditsLeft
      : null;

  let touchpoint = null;
  if (spentCredits === 0) {
    touchpoint = 0;
  } else if (record.creditsAllocated !== null) {
    touchpoint = Math.round(
      (spentCredits / creditsThatShouldHaveBeenSpent) * 100
    );
    if (touchpoint > 100) {
      touchpoint = 100;
    }
  }

  const allowedToSelectRecord =
    isAdmin ||
    level === 2 ||
    (level === 1 &&
      accountManagement &&
      accountManagement.manager &&
      accountManagement.manager.some(m => m.id === record.id));

  return (
    <tr
      onClick={() => {
        if (allowedToSelectRecord && isRecordHovered) {
          onSelectRecord();
        }
      }}
      className={`${styles.record} ${
        allowedToSelectRecord && isRecordHovered ? styles.isRecordHovered : ''
      }`}
      onMouseEnter={() => {
        if (allowedToSelectRecord) setIsRecordHovered(true);
      }}
      onMouseLeave={() => {
        if (allowedToSelectRecord) setIsRecordHovered(false);
      }}
    >
      <td className={styles.rowContainer}>
        {record.isManager ? (
          <img
            src={startIcon}
            alt="Starred user"
            className={styles.starredUser}
          />
        ) : null}
        <span className={styles.nameContainer}>{record.name}</span>
      </td>
      <td className={`${styles.rowContainer} ${styles.touchpointsContainer}`}>
        {touchpoint !== null ? (
          <>
            <div className={styles.touchpointNumber}>{touchpoint}%</div>
            <div className={styles.barContainer}>
              <div
                className={styles.bar}
                style={{
                  width: `${touchpoint}%`,
                  backgroundColor: getAccountManagementColor(touchpoint)
                }}
              />
            </div>
          </>
        ) : null}
      </td>
      {level === 1 ? (
        <>
          <td className={styles.rowContainer}>{licenceStartDateString}</td>
          <td className={styles.rowContainer}>
            {licenceEndDateString}
            {daysLeftToExpireInteger !== null &&
            daysLeftToExpireInteger < 60 ? (
              <div className={styles.warningTooltipContainer}>
                <img
                  className={styles.warningTooltipIcon}
                  alt="Touchpoints tooltip"
                  src={amWarning}
                />
                <div className={styles.warningTooltipPopup}>
                  <div className={styles.warningTooltipPopupTriangle} />
                  License will expire in {daysLeftToExpireInteger}{' '}
                  {daysLeftToExpireInteger === 1 ? 'day' : 'days'}
                </div>
              </div>
            ) : null}
          </td>
        </>
      ) : null}
      <td className={styles.rowContainer}>
        <div
          className={styles.actionButtonsContainer}
          onMouseEnter={() => {
            if (allowedToSelectRecord) setIsRecordHovered(false);
          }}
          onMouseLeave={() => {
            if (allowedToSelectRecord) setIsRecordHovered(true);
          }}
        >
          <RecordAction
            label={level === 1 ? 'Teams' : 'Members'}
            icon={amTeams}
            onClick={() => {
              if (onRecordClick) {
                document.documentElement.scrollTop = 0;
                onRecordClick(record);
              }
            }}
          />
          {shouldBudgetOverviewBeAvailable ? (
            <RecordAction
              label="Budget overview"
              icon={amDownload}
              onClick={async () => {
                const budget = await restApi().generateAccountManagementBudgetOverview(
                  record.id
                );

                FileSaver.saveAs(
                  budget,
                  `export_budget_overview_${record.id}.csv`,
                  {
                    autoBom: true
                  }
                );
              }}
            />
          ) : null}
          {actionsAllowed ? (
            <>
              <RecordAction
                label="Edit"
                icon={amEdit}
                onClick={() => {
                  setActivePopup({ type: recordActionPopups.EDIT, record });
                }}
              />
              <RecordAction
                label="Delete"
                icon={amDelete}
                onClick={() => {
                  setActivePopup({ type: recordActionPopups.DELETE, record });
                }}
              />
            </>
          ) : null}
        </div>
      </td>
    </tr>
  );
};
